<template>
  <bottom-sheet
    v-if="isMobile && !isHiddenByTwoFa"
    :visible="props.visible"
    :show-close-button="true"
    :close-on-backdrop="true"
    @close="emit('close')"
  >
    <div class="modal modal--bottom-sheet">
      <template v-if="hasSlot('custom')">
        <slot name="custom"></slot>
      </template>
      <template v-else>
        <div v-if="hasSlot('header')" class="modal__header">
          <slot name="header"></slot>
        </div>
        <div v-if="hasSlot('body')" class="modal__body">
          <slot name="body"></slot>
        </div>
        <div v-if="hasSlot('footer')" class="modal__footer">
          <slot name="footer" :on-cancel="onClose"></slot>
        </div>
      </template>
    </div>
  </bottom-sheet>

  <base-modal
    v-else
    :name="name"
    :visible="props.visible"
    close-button
    :close-outside="false"
    :classes="classes"
    :before-open="onBeforeOpen"
    :before-close="onBeforeClose"
    class="dialog"
    @close="emit('close')"
  >
    <div class="modal">
      <template v-if="hasSlot('custom')">
        <slot name="custom"></slot>
      </template>
      <template v-else>
        <div v-if="hasSlot('header')" class="modal__header">
          <slot name="header"></slot>
        </div>
        <div v-if="hasSlot('body')" class="modal__body">
          <slot name="body"></slot>
        </div>
        <div v-if="hasSlot('footer')" class="modal__footer">
          <slot name="footer" :on-cancel="onClose"></slot>
        </div>
      </template>
    </div>
    <template v-if="$slots.banner" #banner>
      <slot name="banner"></slot>
    </template>
  </base-modal>
</template>

<script setup>
import { useSlots, ref, watch, onMounted, onUnmounted } from 'vue'
import BaseModal from '@emcd-shared/base-modal/BaseModal.vue'
import BottomSheet from '@/widgets/ModalPaymentLink/ui/components/BottomSheet.vue'

const props = defineProps([
  'name',
  'visible',
  'classes',
  'beforeOpen',
  'beforeClose',
  'opened',
  'closed',
])
const emit = defineEmits(['close'])

const isMobile = ref(false)
const isHiddenByTwoFa = ref(false)

function checkMobile() {
  isMobile.value = window.innerWidth < 768
}

function onTwoFaOpen() {
  if (isMobile.value) isHiddenByTwoFa.value = true
}

function onTwoFaClose() {
  isHiddenByTwoFa.value = false
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  window.addEventListener('auth:2fa-modal-opened', onTwoFaOpen)
  window.addEventListener('auth:2fa-modal-closed', onTwoFaClose)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  window.removeEventListener('auth:2fa-modal-opened', onTwoFaOpen)
  window.removeEventListener('auth:2fa-modal-closed', onTwoFaClose)
})

watch(
  () => props.visible,
  (isVisible) => {
    if (!isMobile.value) return
    if (isVisible) {
      props.beforeOpen?.()
    } else {
      props.beforeClose?.()
    }
  }
)

const slots = useSlots()
function hasSlot(name) {
  return !!slots[name]
}
function onBeforeOpen(params) {
  ;[...document.querySelectorAll('html, body')].forEach((el) => {
    // eslint-disable-next-line no-param-reassign
    el.style.overflow = 'hidden'
  })
  if (props.beforeOpen) {
    props.beforeOpen(params)
  }
}

function onBeforeClose() {
  ;[...document.querySelectorAll('html, body')].forEach((el) => {
    // eslint-disable-next-line no-param-reassign
    el.style.overflow = ''
  })
  if (props.beforeOpen) {
    props.beforeClose()
  }
}
</script>

<style scoped>
.modal {
  background: var(--bg-2);
  border: 1px solid var(--bg-3);
  border-radius: 10px;
  width: min(600px, 100%);
  padding: 30px;
}

.modal--bottom-sheet {
  border: none;
  border-radius: 0;
  width: 100%;
  padding: 0;
}

/* to prevent the default browser styling for the dialog element */
.dialog {
  border: none;
  padding: 0;
  background: none;
  color: unset;
  max-width: unset;
  max-height: calc(
    100% - 20px
  ); /* fix problem with BaseModal for small height phones */
  margin-bottom: auto;
}

.modal__header {
  font-weight: 700;
  font-size: 1.725rem;
  margin-bottom: 30px;
  width: 100%;
  line-height: 1;
}

.modal__footer {
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: none;
  flex-wrap: wrap;
  width: 100%;
}
</style>
